import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useCart } from '../contexts/CartContext';
import { CONFIG, authFetch } from '../utils/config';
import { useContent } from '../hooks/useContent';
import SimplifiedAIQREditor from '../components/SiplifiedAIQREditor';

const Dashboard = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { addToCart, isAuthenticated } = useCart();
  const [shopQRCodes, setShopQRCodes] = useState([]);
  const [loadingShop, setLoadingShop] = useState(false);
  const [addingToCart, setAddingToCart] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    const fetchShopQRCodes = async () => {
      try {
        setLoadingShop(true);
        const params = new URLSearchParams({
          miniature: 'true',
          miniature_quality: '80',
          miniature_size: '256',
          page: '1',
          limit: '4'
        });
        const response = await fetch(`${CONFIG.BASE_URL}/qr-code-shop?${params.toString()}`);
        if (response.ok) {
          const data = await response.json();
          const qrCodesList = Array.isArray(data?.qr_codes) ? data.qr_codes : [];
          setShopQRCodes(qrCodesList.slice(0, 4));
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des QR codes du shop:', err);
      } finally {
        setLoadingShop(false);
      }
    };

    fetchShopQRCodes();
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleAddToCart = async (qrCode) => {
    if (!isAuthenticated()) {
      navigate('/login?redirect=/dashboard');
      return;
    }

    setAddingToCart(qrCode.uuid);
    const result = await addToCart(qrCode.uuid);

    if (result.success) {
      setShopQRCodes(prev => prev.filter(item => item.uuid !== qrCode.uuid));
      setNotification({ type: 'success', message: content.sections.shop.notification.success });
    } else {
      setNotification({ type: 'error', message: result.message });
    }

    setAddingToCart(null);
  };

  const { content, loading: contentLoading } = useContent('dashboard');

  if (contentLoading || !content) {
    return (
      <div className={`pt-16 min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? '' : 'bg-gray-50'}`}>
      {notification && (
        <div className={`fixed top-20 right-4 z-50 p-4 rounded-xl shadow-lg transition-all duration-300 ${
          notification.type === 'success'
            ? isDark ? 'bg-green-900/90 border border-green-500/30 text-green-300' : 'bg-green-50 border border-green-200 text-green-700'
            : isDark ? 'bg-red-900/90 border border-red-500/30 text-red-300' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          <div className="flex items-center gap-2">
            {notification.type === 'success' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <span>{notification.message}</span>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        <div className="mb-6 md:mb-8">
          <h1 className={`text-2xl md:text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {content.pageTitle}
          </h1>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
            {content.pageSubtitle}
          </p>
        </div>

        <div className={`border rounded-xl p-4 md:p-6 mb-6 ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <SimplifiedAIQREditor onUserAcceptsQuikRCode={() => {
            navigate('/dashboard/my-codes');
          }} />
        </div>

        {shopQRCodes.length > 0 && (
          <div className={`border rounded-xl p-4 md:p-6 mt-6 md:mt-8 ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-gray-200 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <div>
                  <h2 className={`text-lg md:text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{content.sections.shop.title}</h2>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{content.sections.shop.subtitle}</p>
                </div>
              </div>
              <Link
                to="/dashboard/shop"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isDark
                    ? 'bg-primary-600/20 text-primary-400 hover:bg-primary-600/30 border border-primary-500/30'
                    : 'bg-primary-50 text-primary-600 hover:bg-primary-100 border border-primary-200'
                }`}
              >
                {content.sections.shop.viewAll}
              </Link>
            </div>

            {loadingShop ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {shopQRCodes.map((qrCode) => (
                  <div
                    key={qrCode.uuid}
                    className={`relative rounded-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                      isDark ? 'bg-dark-700 border border-dark-600' : 'bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <div className={`aspect-square p-4 flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-white'}`}>
                      {qrCode.image ? (
                        <img src={qrCode.image} alt={`QR Code ${qrCode.uuid}`} className="w-full h-full object-contain rounded-lg" />
                      ) : qrCode.miniature ? (
                        <img src={`data:image/webp;base64,${qrCode.miniature}`} alt={`QR Code ${qrCode.uuid}`} className="w-full h-full object-contain rounded-lg" />
                      ) : (
                        <div className={`w-full h-full rounded-lg flex items-center justify-center ${isDark ? 'bg-dark-700' : 'bg-gray-100'}`}>
                          <svg className={`w-20 h-20 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 13h6v6H3v-6zm2 2v2h2v-2H5zm13-2h3v2h-3v-2zm-5 0h2v2h-2v-2zm2 2h2v2h-2v-2zm-2 2h2v2h-2v-2zm2 2h2v2h-2v-2zm2-2h3v4h-3v-4z"/>
                          </svg>
                        </div>
                      )}
                    </div>

                    <div className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{content.sections.shop.price}</span>
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{content.sections.shop.tax}</span>
                      </div>
                      <button
                        onClick={() => handleAddToCart(qrCode)}
                        disabled={addingToCart === qrCode.uuid}
                        className="w-full py-2 px-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white text-sm font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {addingToCart === qrCode.uuid ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>{content.sections.shop.adding}</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <span>{content.sections.shop.addButton}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default Dashboard;
